import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Tooltip,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import { Formik } from 'formik';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation } from 'react-query';
import { Link } from 'react-router-dom';
import {
  useRecoilStateLoadable,
  useRecoilValueLoadable,
  useSetRecoilState,
} from 'recoil';
import { useFeatureFlag } from 'src/app/hooks/useFeatureFlag';
import * as Yup from 'yup';

import { LabelledOutline } from '../../common/final-form-material-ui';
import {
  FormikTextField,
  MaterialAutocomplete,
} from '../../common/formik-material-ui';
import { useBusinessPermissions } from '../../common/helperFunctions/useBusinessPermissions';
import { trackSaveEditViewTeam } from '../../common/userTracking';
import { teamAgentIds } from '../../recoil/atoms/teamAgentIds';
import { teamId } from '../../recoil/atoms/teamId';
import { user } from '../../recoil/atoms/user';
import { getTeamNameById } from '../../recoil/selectors/getTeamNameById';
import {
  methodNameEnum,
  refetchToggle,
} from '../../recoil/selectors/makePHPRequest';
import { getApiMutation } from '../../services/rest/getApiMutation';
import {
  endpointEnum,
  makeAPIRequest,
} from '../../services/rest/makeAPIRequest';
import { ModalWithStyles } from '../components/modals/ModalWithStyles';
import dataTableScss from '../modules/Manage/pages/queues-table/data-table.module.css';
import { DataTable } from '../modules/Manage/pages/queues-table/DataTable';

const useStyles = makeStyles((theme) => ({
  field: {
    marginTop: theme.spacing(1), // space between fields
    marginBottom: theme.spacing(1), // space between fields
  },
  deleteIconButton: {
    color: theme?.custom?.button?.delete?.main,
  },
  button: {
    marginTop: '10px',
    marginLeft: '40px',
  },
  actionButton: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
}));

export function ViewTeam(props) {
  const classes = useStyles();
  const { id } = props;
  const [
    ,
    // teamid
    setTeamId,
  ] = useRecoilStateLoadable(teamId);
  const [userAtom, setUserAtom] = useRecoilStateLoadable(user);
  const [deleteGroupId, setDeleteGroupId] = useState('');
  const [confirmedDeleteId, setConfirmedDeleteId] = useState([]);
  const [agentIdsQueued, setAgentIdsQueued] = useRecoilStateLoadable(
    teamAgentIds
  );
  const [sortModel, _setSortModel] = React.useState([
    { field: 'fullName', sort: 'asc' },
  ]);
  const dataUsers = {
    call: methodNameEnum.GET_BUSINESS_USERS,
  };
  const businessUsersLoadable = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: endpointEnum.GET_BUSINESS_USERS,
      payloadJsonString: JSON.stringify(dataUsers),
    })
  );
  const users = useMemo(
    () =>
      businessUsersLoadable.state === 'hasValue'
        ? businessUsersLoadable.contents?.response?.data?.result.filter(
            (user) => user.isactive === 1
          )
        : [],
    [businessUsersLoadable, userAtom]
  );
  const setRefetchToggleForBusinessTeams = useSetRecoilState(
    refetchToggle(endpointEnum.GET_BUSINESS_TEAMS)
  );
  const teamSetting = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: endpointEnum.GET_TEAM_SETTINGS,
      payloadJsonString: JSON.stringify({
        call: methodNameEnum.GET_TEAM_SETTINGS,
        teamid: id,
      }),
    })
  );
  const [userIds, setUserIds] = useState([]);
  const onChangeForAutocomplete = (event, newValue) => {
    // keep autocomplete in sync
    setUserIds(
      newValue // update value for type
    );
  };
  const teamSettingCached = useRef([]);
  teamSettingCached.current = useMemo(
    () =>
      (
        businessUsersLoadable.contents?.response?.data?.result // this is all users
          ?.filter((user) => agentIdsQueued.contents.includes(user.userid)) // get only users found on agentIdsQueued
          ?.map((user) => ({
            email: user.email,
            fname: user.fname,
            lname: user.lname,
            fullName: `${user.fname} ${user.lname}`,
            id: user.userid,
            userEXT: user.userEXT,
            userTier:
              user.userTier === 'Enterprise'
                ? 'Professional'
                : user.userTier === 'Enterprise Unlimited'
                ? 'Professional Unlimited'
                : user.userTier,
            userid: user.userid,
            // insert queue flag to show this member is queued for addition
            queued: true,
          })) ?? []
      )
        .concat(
          teamSetting.contents?.response?.data?.result?.map((o) => ({
            ...o,
            fullName: `${o.fname} ${o.lname}`,
            id: o.userid,
            userTier:
              o.userTier.toLowerCase() === 'enterprise unlimited'
                ? 'Professional Unlimited'
                : o.userTier.toLowerCase() === 'enterprise'
                ? 'Professional'
                : o.userTier,
          })) ?? []
        )
        .filter((member) => !confirmedDeleteId.includes(member.userid)) // hides users pending delete from team
        .sort((a, b) =>
          a?.fullName?.toLowerCase()?.localeCompare(b?.fullName?.toLowerCase())
        ),

    [
      teamSetting,
      id,
      agentIdsQueued,
      confirmedDeleteId,
      userIds,
      businessUsersLoadable,
    ]
  );
  const arrayOfIdsToFilter = teamSettingCached.current?.map(
    (team) => team.userid
  );

  const setRefetchToggleForTeamSettings = useSetRecoilState(
    refetchToggle(endpointEnum.GET_TEAM_SETTINGS)
  );

  useEffect(() => {
    setTeamId(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  const teamNameSelector = useRecoilValueLoadable(getTeamNameById);

  const { treatment } = useFeatureFlag('DASH-228-align-delete-icon');
  const isAlignDeleteIconEnabled = useMemo(
    () => treatment === 'control' || treatment === 'on',
    [treatment]
  );

  function formatter(params) {
    // create Actions element here
    // refactor to group id stuff
    return deleteGroupId === params.row.id ? (
      <div
        {...(isAlignDeleteIconEnabled && { className: classes.actionButton })}
      >
        <article className="row">
          <Tooltip title="Confirm Deletion">
            <IconButton
              aria-label="accept"
              onClick={async (event) => {
                event.preventDefault(); // stop link
                // add to the queue for deletion
                setConfirmedDeleteId([...confirmedDeleteId, deleteGroupId]);
                setDeleteGroupId(undefined); // clear the user pending delete confirmation
              }}
              disabled={mutation.isLoading}
              size="large"
            >
              <CheckIcon color={mutation.isLoading ? 'disabled' : 'primary'} />
              {mutation.isLoading && (
                <CircularProgress
                  size={43}
                  className={classes.buttonProgress}
                />
              )}
            </IconButton>
          </Tooltip>
          <Tooltip title="Cancel">
            <IconButton
              aria-label="cancel"
              onClick={(event) => {
                event.preventDefault(); // stop link
                // reset groupid hide confirm / cancel deletion
                setDeleteGroupId(undefined);
              }}
              disabled={mutation.isLoading}
              size="large"
            >
              <ClearIcon
                color="disabled"
                className={clsx({
                  [classes.deleteIconButton]: !mutation.isLoading, // || deleteCancelTokenSource,
                })}
              />
            </IconButton>
          </Tooltip>
        </article>
      </div>
    ) : (
      <div
        {...(isAlignDeleteIconEnabled && { className: classes.actionButton })}
      >
        <Tooltip title="Delete">
          <IconButton
            style={{ marginLeft: '35px' }}
            aria-label="delete"
            onClick={() => {
              setDeleteGroupId(params.row.id);
            }}
            size="large"
          >
            <DeleteIcon className={classes.deleteIconButton} />
          </IconButton>
        </Tooltip>
      </div>
    );
  }
  const readOnly = useBusinessPermissions().accountReadOnly;
  const columns = [
    {
      field: 'fullName',
      headerName: 'Full Name',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
      renderCell: (params) => {
        return (
          <Tooltip title="View">
            <Link to={`/manage/agents/${params.row.userid}`}>
              {params.value}
            </Link>
          </Tooltip>
        );
      },
    },
    {
      field: 'email',
      headerName: 'Email',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
    },
    {
      field: 'userEXT',
      headerName: 'Ext.',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
    },
    {
      field: 'userTier',
      headerName: 'Tier',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
    },
    {
      field: 'userid',
      headerName: 'Actions',
      headerAlign: 'right',
      hide: readOnly,
      enableSorting: false,
      renderCell: formatter,
    },
  ];
  const mutation = useMutation(getApiMutation(userAtom.contents, setUserAtom));

  const changeTeamNameSchema = useMemo(
    () =>
      Yup.object({
        name: Yup.string().required('Required'),
      }),
    []
  );
  // const ToolbarComponent = useCallback(
  //   (props) => (
  //     <EnhancedTableToolbar
  //       {...props}
  //       numSelected={selected.length}
  //       rightComponent={
  //         <ToolbarAddDeleteButton
  //           numSelected={selected.length}
  //           onClickForAdd={handleShow}
  //           onClickForDelete={handleShowD}
  //           isLoading={props.isLoading}
  //         />
  //       }
  //     />
  //   ),
  //   [handleShow, handleShowD, selected.length]
  // );
  function onHideFunction() {
    props.onHide();
    //  reset all queued values on cancel
    setConfirmedDeleteId([]);
    setAgentIdsQueued([]);
    setUserIds([]);
    setDeleteGroupId('');
  }
  function addToQueue() {
    const idsToAdd = userIds.map((user) => user.userid);
    const queuedIds = idsToAdd
      .filter((userid) => !confirmedDeleteId.includes(userid)) // do not include userids which can be removed from the confirmedDeleteId array
      .concat(agentIdsQueued.contents);

    // if an added userid was deleted previously then re-add it
    // by removing the userid from the confirmedDeleteId array
    const newConfirmedDeletedIds = confirmedDeleteId.filter(
      (userid) => !idsToAdd.includes(userid)
    );

    setConfirmedDeleteId(newConfirmedDeletedIds);
    setAgentIdsQueued(queuedIds);
    setUserIds([]);
  }
  return (
    <Paper square={true} elevation={0}>
      <Formik
        onSubmit={async (values, actions) => {
          // find new team members queued
          const newValues = teamSettingCached.current.filter(
            (member) => member.queued
          );
          const idArr = newValues.flatMap(({ userid }) => [userid]);
          if (idArr.length >= 1) {
            // if the array of new member ids is valid make requests with those ids to add to the team
            // const url = `${
            //   process.env.VITE_APP_PHP_DOMAIN
            // }/rest/functions.php?call=addAgentsTeam&apiKey=${
            //   userAtom.contents.apikey
            // }&businessid=${
            //   userAtom.contents.businessid
            // }&teamid=${id}&ids=${encodeURI(JSON.stringify(idArr))}`;
            // await Axios.post(url, null, { withCredentials: true });
            const data = {
              call: methodNameEnum.ADD_AGENT_TEAM,
              apikey: userAtom.contents.apikey,
              businessid: userAtom.contents.businessid,
              teamid: id,
              ids: JSON.stringify(idArr),
            };
            const response = await mutation.mutateAsync({
              endpoint: endpointEnum.ADD_AGENT_TEAM,
              payload: data,
            });
            if (response?.data?.success) {
              props.enqueueSnackbarWithStyles({
                message: 'Successfully Added Team Member(s)',
                alertProps: { severity: 'success' },
              });
            } else {
              props.enqueueSnackbarWithStyles({
                message: 'Error Adding Team Member(s)',
                alertProps: { severity: 'warning' },
              });
            }
          }
          // if there are ids queued for deletion
          if (confirmedDeleteId.length >= 1) {
            for (let i = 0; i < confirmedDeleteId.length; i++) {
              //  iterate through the array and deltee them one by one
              // const url = `${process.env.VITE_APP_PHP_DOMAIN}/rest/functions.php?call=deleteTeamMember&businessid=${userAtom.contents.businessid}&apiKey=${userAtom.contents.apikey}&userid=${confirmedDeleteId[i]}&teamid=${id}`;
              // await Axios.post(url, null, { withCredentials: true });
              const data = {
                call: methodNameEnum.DELETE_TEAM_MEMBER,
                apikey: userAtom.contents.apikey,
                deleteuserid: confirmedDeleteId[i],
                businessid: userAtom.contents.businessid,
                teamid: id,
              };
              await mutation.mutateAsync({
                endpoint: endpointEnum.DELETE_TEAM_MEMBER,
                payload: data,
              });
            }
            props.enqueueSnackbarWithStyles({
              message: 'Successfully Deleted  Team Member(s)',
              alertProps: { severity: 'success' },
            });
          }
          // if the original name is different than the new name
          let mutationResponse = {};
          if (teamNameSelector.contents !== values.name) {
            mutationResponse = await mutation.mutateAsync({
              endpoint: endpointEnum.UPDATE_TEAM_NAME,
              payload: {
                call: methodNameEnum.UPDATE_TEAM_NAME,
                apikey: userAtom.contents.apikey,
                teamid: id,
                name: values.name,
              },
            });
            if (mutationResponse?.data?.success) {
              props.enqueueSnackbarWithStyles({
                message: 'Successfully Updated Team Name',
                alertProps: { severity: 'success' },
              });
              // refetch data for teams and settings
              setRefetchToggleForBusinessTeams({});
              setRefetchToggleForTeamSettings({});
              onHideFunction();
            } else {
              props.enqueueSnackbarWithStyles({
                message: 'Team Name already exists',
                alertProps: { severity: 'error' },
              });
            }
          }

          // refetch data for teams and settings
          setRefetchToggleForBusinessTeams({});
          setRefetchToggleForTeamSettings({});
          onHideFunction();
        }}
        enableReinitialize={true}
        validationSchema={changeTeamNameSchema}
        initialValues={{
          name:
            teamNameSelector.state === 'hasValue'
              ? teamNameSelector.contents ?? ''
              : '',
        }}
      >
        {(formik) => (
          <ModalWithStyles
            onSubmit={() => {
              formik.handleSubmit();
              trackSaveEditViewTeam();
            }}
            title="View Team"
            aria-labelledby="example-modal-sizes-title-lg"
            show={props.show}
            onHide={onHideFunction}
            isSubmitting={formik.isSubmitting}
            isLoading={formik.isSubmitting}
            disableSubmit={
              !formik.dirty &&
              agentIdsQueued.contents.length === 0 &&
              confirmedDeleteId.length === 0
            }
          >
            <FormikTextField
              name="name"
              className={classes.field}
              formik={formik}
              fullWidth
              label="Team Name"
              size="small"
              inputProps={{ maxLength: 45 }}
              required
              variant="outlined"
            />
            <LabelledOutline label="Team Members">
              <div className="row">
                <div className="col-sm-10">
                  <MaterialAutocomplete
                    type="text"
                    fullWidth
                    label="Team Member"
                    size="small"
                    variant="outlined"
                    multiple={true}
                    options={
                      users
                        ?.filter(
                          (user) => !arrayOfIdsToFilter?.includes(user.userid)
                        )
                        ?.sort((a, b) =>
                          a?.fname
                            ?.toLowerCase()
                            ?.localeCompare(b?.fname?.toLowerCase())
                        ) ?? []
                    }
                    value={userIds}
                    onChange={onChangeForAutocomplete}
                    getOptionLabel={(user) =>
                      user?.fname + ' ' + user.lname ?? ''
                    }
                    isOptionEqualToValue={(user, value) =>
                      user?.userid === value?.userid
                    }
                    renderOption={(props, option) => {
                      const optionProps = { ...props, key: option.userid };
                      return (
                        <li {...optionProps}>
                          {`${option?.fname} ${option?.lname}`}
                        </li>
                      );
                    }}
                  />
                </div>
                <div className="col-sm-2">
                  <Button
                    className={classes.button}
                    name="Add"
                    variant="contained"
                    startIcon={<AddIcon />}
                    color="primary"
                    onClick={addToQueue}
                    disableElevation
                    disabled={
                      teamNameSelector.state !== 'hasValue' || !userIds.length
                    }
                  >
                    Add
                  </Button>
                </div>
              </div>
              <DataTable
                // setSelected={setSelected}
                columns={columns}
                loading={teamSetting.state === 'loading'}
                rows={teamSettingCached?.current ?? []}
                sortModel={sortModel}
                // ToolbarComponent={ToolbarComponent}
              />
            </LabelledOutline>
          </ModalWithStyles>
        )}
      </Formik>
    </Paper>
  );
}
